'use client';

import { useMemo } from 'react';

import { usePersonalizationTracker, useCallbackRef } from '@skin-seoul/utils';

import {
  ANALYTICS_EVENT_TYPE,
  type AnalyticsContext,
  type AnalyticsEventRequest,
  type AnalyticsEventType,
  type CheckoutContext,
  type ConsentState,
  type CustomEventContext,
  type InteractionContext,
  type ProductContext,
} from '../../infrastructure/analytics';
import { useAnalyticsPlatformContext } from '../../context/analyticsPlatformContext';
import { useAnalyticsService } from './useAnalyticsService';

export interface AnalyticsPlatformContext {
  platform: 'web' | 'app';
  clientId?: string;
  userId?: string;
  pagePath?: string;
  pageTitle?: string;
  pageReferrer?: string;
  consent?: ConsentState;
  osName?: string;
  deviceModel?: string;
  appVersion?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
  /**
   * Called after each analytics event is sent.
   * Used by mobile to clear one-time UTM after first event (match web behavior).
   */
  onEventSent?: () => void;
}

export interface TrackViewParams {
  productId?: string;
  pageType?: string;
  /** Extra params merged into event_properties via customEvent.params on the server */
  params?: Record<string, unknown>;
}

export interface TrackAddToCartParams {
  productId: string;
  quantity?: number;
  variant?: string;
}

export interface TrackRemoveFromCartParams {
  productId: string;
  quantity?: number;
  variant?: string;
}

export interface TrackPurchaseParams {
  orderId: string;
  checkout?: CheckoutContext;
}

export interface TrackImpressionParams {
  productId: string;
  sectionName?: string;
}

export interface TrackClickParams {
  productId: string;
  sectionName?: string;
}

export interface TrackAuthParams {
  authenticationMethod: string;
}

export interface TrackCustomParams {
  ga4EventName?: string;
  amplitudeEventName?: string;
  params?: Record<string, unknown>;
}

export function useAnalyticsEvent() {
  const resolvedContext = useAnalyticsPlatformContext();

  if (!resolvedContext) {
    throw new Error('[useAnalyticsEvent] AnalyticsPlatformProvider not found. Wrap your app with AnalyticsPlatformProvider.');
  }
  const tracker = usePersonalizationTracker();
  const { useTrackEvent } = useAnalyticsService();
  const { mutate } = useTrackEvent();

  const sendEvent = useCallbackRef(
    (
      eventType: AnalyticsEventType,
      analytics: Omit<
        AnalyticsContext,
        'clientId' | 'pagePath' | 'pageTitle' | 'pageReferrer' | 'platform' | 'consent'
      > = {},
      options?: { productId?: string; orderId?: string },
    ) => {
      // Try in-memory actorId first, fall back to cookie if PersonalizationClient hasn't initialized yet
      const actorId = tracker.getActorId() ?? (() => {
        if (typeof document === 'undefined') return null;
        const match = document.cookie.match(/(?:^|;\s*)ss_actor_id=([^;]*)/);
        return match?.[1] ?? null;
      })();

      if (!actorId) {
        console.warn(`[Analytics] Skipped ${eventType}: actorId is null`, {
          platform: resolvedContext.platform,
          clientId: resolvedContext.clientId,
          consent: resolvedContext.consent,
        });
        return;
      }

      // Read pagePath/pageTitle at send time (not render time) to avoid stale values during SPA navigation
      const currentPagePath = typeof window !== 'undefined' && window.location ? window.location.href : resolvedContext.pagePath;
      const currentPageTitle = typeof window !== 'undefined' && typeof document !== 'undefined' ? document.title : resolvedContext.pageTitle;

      const request: AnalyticsEventRequest = {
        actorId,
        eventType,
        analytics: {
          clientId: resolvedContext.clientId || actorId,
          userId: resolvedContext.userId,
          pagePath: currentPagePath,
          pageTitle: currentPageTitle,
          pageReferrer: resolvedContext.pageReferrer,
          platform: resolvedContext.platform,
          consent: resolvedContext.consent,
          osName: resolvedContext.osName,
          deviceModel: resolvedContext.deviceModel,
          appVersion: resolvedContext.appVersion,
          utmSource: resolvedContext.utmSource,
          utmMedium: resolvedContext.utmMedium,
          utmCampaign: resolvedContext.utmCampaign,
          utmContent: resolvedContext.utmContent,
          utmTerm: resolvedContext.utmTerm,
          ...analytics,
        },
        ...options,
      };

      console.log(`[Analytics] ${eventType}`, JSON.stringify(request, null, 2));

      mutate(request, {
        onError: (error) => {
          console.error(`[Analytics] Failed to send ${eventType}`, JSON.stringify(error, Object.getOwnPropertyNames(error), 2));
        },
      });
      resolvedContext.onEventSent?.();
    },
  );

  return useMemo(
    () => ({
      trackView: ({ productId, pageType, params }: TrackViewParams = {}) => {
        sendEvent(
          ANALYTICS_EVENT_TYPE.VIEW,
          {
            pageType,
            ...(params ? { customEvent: { params } } : {}),
          },
          { productId },
        );
      },

      trackAddToCart: ({ productId, quantity, variant }: TrackAddToCartParams) => {
        const product: ProductContext = { id: productId, quantity, variant };
        sendEvent(ANALYTICS_EVENT_TYPE.ADD_TO_CART, { product }, { productId });
      },

      trackRemoveFromCart: ({ productId, quantity, variant }: TrackRemoveFromCartParams) => {
        const product: ProductContext = { id: productId, quantity, variant };
        sendEvent(ANALYTICS_EVENT_TYPE.REMOVE_FROM_CART, { product }, { productId });
      },

      trackPurchase: ({ orderId, checkout }: TrackPurchaseParams) => {
        sendEvent(ANALYTICS_EVENT_TYPE.PURCHASE, { checkout }, { orderId });
      },

      trackImpression: ({ productId, sectionName }: TrackImpressionParams) => {
        const interaction: InteractionContext | undefined = sectionName
          ? { sectionName }
          : undefined;
        sendEvent(ANALYTICS_EVENT_TYPE.IMPRESSION, { interaction }, { productId });
      },

      trackClick: ({ productId, sectionName }: TrackClickParams) => {
        const interaction: InteractionContext | undefined = sectionName
          ? { sectionName }
          : undefined;
        sendEvent(ANALYTICS_EVENT_TYPE.CLICK, { interaction }, { productId });
      },

      trackHomePageView: () => {
        sendEvent(ANALYTICS_EVENT_TYPE.HOME_PAGE_VIEW);
      },

      trackBrowsing: () => {
        sendEvent(ANALYTICS_EVENT_TYPE.BROWSING);
      },

      trackLogin: ({ authenticationMethod }: TrackAuthParams) => {
        const interaction: InteractionContext = { authenticationMethod };
        sendEvent(ANALYTICS_EVENT_TYPE.LOGIN, { interaction });
      },

      trackSignup: ({ authenticationMethod }: TrackAuthParams) => {
        const interaction: InteractionContext = { authenticationMethod };
        sendEvent(ANALYTICS_EVENT_TYPE.SIGNUP, { interaction });
      },

      trackCustom: ({ ga4EventName, amplitudeEventName, params }: TrackCustomParams) => {
        const customEvent: CustomEventContext = { ga4EventName, amplitudeEventName, params };
        sendEvent(ANALYTICS_EVENT_TYPE.CUSTOM, { customEvent });
      },
    }),
    [sendEvent],
  );
}
